import { useState, useEffect, useCallback } from 'react';
import { getTodayStatus, getAdminMonths, getConfirmMonthPreview, getDetections, AdminLocationInfo, TodayStatus, AdminMonth } from '../api/adminApi';
import { getApproximateHijriToday } from '../utils/approximateHijri';

export type CardStatus = 'gray' | 'white' | 'yellow' | 'green' | 'red';

export interface CountryOverview {
  location: AdminLocationInfo;
  status: TodayStatus | null;
  months: AdminMonth[];
  pendingDetections: number;
  cardStatus: CardStatus;
  tomorrowMessage?: string;
  // Whether this country has ANY confirmed month on record, ever — not just within
  // the current/next Hijri year window `months` covers. A country can have real
  // (but stale/gapped) history that `months` alone would miss entirely.
  hasAnyConfirmedMonth: boolean;
  // kept for backward compat
  signal: 'all-clear' | 'watch' | 'act';
  daysToWindow: number | null;
}

const HIJRI_MONTHS = [
  '', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah',
];

function getMonthName(index: number): string {
  return HIJRI_MONTHS[index] ?? `Month ${index}`;
}

function addDays(gregorianDate: string, days: number): string {
  const d = new Date(gregorianDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function isWithin24h(isoTimestamp: string): boolean {
  return Date.now() - new Date(isoTimestamp).getTime() < 24 * 60 * 60 * 1000;
}

function hasNextMonthConfirmed(status: TodayStatus, months: AdminMonth[]): boolean {
  const cur = status.date.month.index;
  const nextIndex = cur === 12 ? 1 : cur + 1;
  const nextYear = cur === 12 ? status.date.year + 1 : status.date.year;
  return months.some(
    (m) => m.year === nextYear && m.monthIndex === nextIndex && m.status === 'CONFIRMED'
  );
}

function deriveCardStatus(
  status: TodayStatus | null,
  months: AdminMonth[],
  hasAnyConfirmedMonth: boolean
): { cardStatus: CardStatus; tomorrowMessage?: string } {
  if (!hasAnyConfirmedMonth || !status) return { cardStatus: 'gray' };

  const day = status.date.day;
  const curIdx = status.date.month.index;
  const curYear = status.date.year;
  const nextIdx = curIdx === 12 ? 1 : curIdx + 1;
  const nextYear = curIdx === 12 ? curYear + 1 : curYear;

  // Green only when the NEXT month was recently confirmed (not just any month).
  // Initialization confirms the current month's start — that must not trigger green.
  const recentNextMonth = months.find(
    (m) => m.monthIndex === nextIdx && m.year === nextYear && m.confirmedAt && isWithin24h(m.confirmedAt)
  );
  if (recentNextMonth) {
    let tomorrowMessage: string | undefined;
    if (status.gregorianDate && recentNextMonth.startDate) {
      const tomorrow = addDays(status.gregorianDate, 1);
      const dayAfter = addDays(status.gregorianDate, 2);
      if (recentNextMonth.startDate === tomorrow) {
        tomorrowMessage = `Tomorrow is 1 ${getMonthName(nextIdx)} ${nextYear}`;
      } else if (recentNextMonth.startDate === dayAfter) {
        tomorrowMessage = `Tomorrow is 30 ${status.date.month.en}`;
      }
    }
    return { cardStatus: 'green', tomorrowMessage };
  }

  // Inconsistent: day > 30
  if (day > 30) return { cardStatus: 'red' };

  // Inconsistent: day 30 without a confirmed next month
  if (day === 30 && !hasNextMonthConfirmed(status, months)) return { cardStatus: 'red' };

  // Needs confirmation: day 29, crescent not yet confirmed
  if (day === 29 && !hasNextMonthConfirmed(status, months)) return { cardStatus: 'yellow' };

  // Day 0 = boundary/unknown but initialized — inconsistent state
  if (day === 0) return { cardStatus: 'red' };

  // Stable
  return { cardStatus: 'white' };
}

// Legacy signal kept for backward compat with DetectionsPage
function deriveSignal(status: TodayStatus | null, pending: number): 'all-clear' | 'watch' | 'act' {
  if (pending > 0) return 'act';
  if (!status) return 'all-clear';
  const day = status.date.day;
  if (day === 0 || day >= 28) return 'watch';
  return 'all-clear';
}

function deriveDaysToWindow(status: TodayStatus | null): number | null {
  if (!status || status.date.day === 0) return null;
  const day = status.date.day;
  if (day >= 28) return null;
  return 28 - day;
}

export function useAllCountries(locations: AdminLocationInfo[]) {
  const managed = locations.filter((l) => l.enabled && l.method !== 'CALCULATION');
  const [overviews, setOverviews] = useState<CountryOverview[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (managed.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      const approx = getApproximateHijriToday();
      const [allPending, ...countryData] = await Promise.all([
        getDetections('PENDING'),
        ...managed.map((l) =>
          Promise.all([
            getTodayStatus(l.code),
            getAdminMonths(l.code),
            getConfirmMonthPreview(l.code, approx.year, approx.month.index),
          ]).catch(
            () => [null, [], null] as [null, AdminMonth[], null]
          )
        ),
      ]);

      const pendingByLocation: Record<string, number> = {};
      allPending.forEach((d) => {
        pendingByLocation[d.locationCode] = (pendingByLocation[d.locationCode] ?? 0) + 1;
      });

      const result: CountryOverview[] = managed.map((loc, i) => {
        const [status, months, preview] = countryData[i] as [
          TodayStatus | null,
          AdminMonth[],
          Awaited<ReturnType<typeof getConfirmMonthPreview>> | null
        ];
        const pending = pendingByLocation[loc.code] ?? 0;
        // Fall back to the year-windowed `months` list if the preview call failed —
        // less accurate (misses history outside the window) but keeps the dashboard usable.
        const hasAnyConfirmedMonth = preview
          ? preview.previousConfirmed !== null
          : months.some((m) => m.status === 'CONFIRMED');
        const { cardStatus, tomorrowMessage } = deriveCardStatus(status, months, hasAnyConfirmedMonth);
        return {
          location: loc,
          status,
          months,
          pendingDetections: pending,
          cardStatus,
          tomorrowMessage,
          hasAnyConfirmedMonth,
          signal: deriveSignal(status, pending),
          daysToWindow: deriveDaysToWindow(status),
        };
      });

      // Priority sort: red > yellow > green > white > gray
      const priority: Record<CardStatus, number> = { red: 0, yellow: 1, green: 2, white: 3, gray: 4 };
      result.sort((a, b) => priority[a.cardStatus] - priority[b.cardStatus]);

      setOverviews(result);
    } catch {
      // keep stale data
    } finally {
      setLoading(false);
    }
  }, [managed.map((l) => l.code).join(',')]);

  useEffect(() => { fetch(); }, [fetch]);

  return { overviews, loading, refetch: fetch };
}
